import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './atoms/Icon';
import { getCandidateProfile, request } from '../services/api';
import SessionManager from '../utils/sessionManager';

const ProfileCompletionPrompt = () => {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const checkProfileCompletion = async () => {
      const session = SessionManager.getCurrentSession();
      
      // Only check for candidates
      if (!session || session.role !== 'candidate') {
        return;
      }

      // Check if user has dismissed this prompt in this session
      const dismissedInSession = sessionStorage.getItem('profile_prompt_dismissed');
      if (dismissedInSession) {
        return;
      }

      try {
        const candidateId = session.user_id;
        
        // First, check if profile credits have been granted (means profile is complete)
        try {
          const creditsResponse = await request(`/credits/balance?candidate_id=${candidateId}`);
          if (creditsResponse && creditsResponse.profile_credits_granted) {
            // Profile is complete, don't show prompt
            console.log('Profile already complete (credits granted)');
            return;
          }
        } catch (creditsError) {
          console.log('Could not check credits, will check profile directly');
        }
        
        // Check profile completion
        const profileResponse = await getCandidateProfile(candidateId);
        
        if (profileResponse && profileResponse.profile) {
          const profile = profileResponse.profile;
          
          // Calculate completion percentage based on backend logic
          // Profile is complete if has full_name, phone, and (education OR work_experience OR skills)
          let completed = 0;
          let total = 4; // name, phone, email, and at least one detail section
          
          if (profile.full_name) completed++;
          if (profile.phone) completed++;
          if (profile.email) completed++;
          
          // Check if has at least one detail section
          const hasEducation = profile.education && profile.education.length > 0;
          const hasWork = profile.work_experience && profile.work_experience.length > 0;
          const hasSkills = profile.skills && profile.skills.length > 0;
          
          if (hasEducation || hasWork || hasSkills) completed++;
          
          const percentage = Math.round((completed / total) * 100);
          setCompletionPercentage(percentage);
          
          // Show prompt if profile is less than 100% complete
          if (percentage < 100) {
            setShow(true);
          }
        } else {
          // No profile found, show prompt
          setCompletionPercentage(0);
          setShow(true);
        }
      } catch (error) {
        console.error('Error checking profile completion:', error);
        // If profile doesn't exist (404), show the prompt
        if (error.message?.includes('404') || error.message?.includes('not found')) {
          setCompletionPercentage(0);
          setShow(true);
        }
      }
    };

    checkProfileCompletion();
  }, []);

  const handleComplete = () => {
    navigate('/onboarding');
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShow(false);
    // Remember dismissal for this session
    sessionStorage.setItem('profile_prompt_dismissed', 'true');
  };

  if (!show || dismissed) {
    return null;
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 max-w-md animate-slide-up">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                <Icon name="person" size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg">Complete Your Profile</h3>
                <p className="text-blue-100 text-sm">{completionPercentage}% Complete</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="text-white/80 hover:text-white transition-colors"
            >
              <Icon name="close" size={20} />
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="px-6 pt-4">
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className="bg-gradient-to-r from-blue-600 to-purple-600 h-full transition-all duration-500"
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <p className="text-gray-600 text-sm mb-4">
            {completionPercentage === 0
              ? "Get started by creating your profile to unlock all features and opportunities."
              : "You're almost there! Complete your profile to access all features and get matched with better opportunities."}
          </p>

          <div className="space-y-2 mb-4">
            <div className="flex items-center space-x-2 text-sm">
              <Icon name="check_circle" size={16} className="text-green-500" />
              <span className="text-gray-700">Get personalized job recommendations</span>
            </div>
            <div className="flex items-center space-x-2 text-sm">
              <Icon name="check_circle" size={16} className="text-green-500" />
              <span className="text-gray-700">Unlock AI interview training</span>
            </div>
            <div className="flex items-center space-x-2 text-sm">
              <Icon name="check_circle" size={16} className="text-green-500" />
              <span className="text-gray-700">Earn 100 free credits</span>
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={handleComplete}
              className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2.5 rounded-lg font-semibold hover:shadow-lg transition-all"
            >
              Complete Now
            </button>
            <button
              onClick={handleDismiss}
              className="px-4 py-2.5 text-gray-600 hover:text-gray-800 font-medium transition-colors"
            >
              Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileCompletionPrompt;
